'use client';

import { FunctionComponent } from 'react';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { Button } from '@/react/components/core-ui/Button';
import { Icon, IconSizeProp, IconTypes } from '@/react/components/core-ui/Icon';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import { useLocale } from '@/react/hooks/useLocale';
import { Link } from '../../../core-ui/Link';

import S from './styles.base.module.scss';
import { footer_cookiePreferences } from "@/lang/__generated__/ahnu/footer_cookiePreferences";

/**
 * Links to legal documentation pages and accessibility.
 */
export const FooterBottom: FunctionComponent = () => {
  const [msg] = useLocaleMessage();
  const { language, country } = useLocale();

  const config = ConfigurationService.getConfig('footer', language, country);
  const osanoConfig = ConfigurationService.getConfig(
    'osano',
    language,
    country
  );
  const osanoEnabled = osanoConfig.getSetting('enabled').value;
  const footerSubMenu = config.getSetting('footerSubMenu').value;
  const accessibility = footerSubMenu.find((item) => {
    return item.id.value === 'accessibility';
  });

  const flagIconType = `${country}Flag` as keyof typeof IconTypes;

  /**
   * Map footer item ID's to specific style classes.
   * This allows for custom styling of specific footer items based on their IDs.
   * Example, the 'international' link item can have a specific style applied.
   */
  const mapFooterItemToStyle: Record<string, string> = {
    international: S.international
  };

  /**
   * Helper function that returns a className for a given footer item ID.
   * @param id - The ID of the footer item.
   * @returns A className string based on the ID, or an empty string if no match is found.
   */
  const getFooterItemStyle = (id: string): string => {
    return mapFooterItemToStyle[id] ?? '';
  };

  const footerSubMenuItems = footerSubMenu.map(
    ({ id, label, href, newTab }) => {
      const itemClass = getFooterItemStyle(id.value);

      if (id.value === 'cookies-preference' && osanoEnabled) {
        return (
          <li className={classes(S.item, itemClass)} key={id.value}>
            <Button
              variant="text"
              onClick={() =>
                window.Osano.cm.showDrawer('osano-cm-dom-info-dialog-open')
              }
              className={S.link}
            >
              {msg(footer_cookiePreferences)}
            </Button>
          </li>
        );
      }

      return (
        <li className={classes(S.item, itemClass)} key={id.value}>
          <Link
            variant="text"
            href={href.value}
            className={S.link}
            openInNewTab={newTab.value}
          >
            {id.value === 'international' && (
              <Icon
                className={classes(S.icon, S.flag)}
                icon={IconTypes[flagIconType]}
                size={IconSizeProp.SizeSM}
              />
            )}
            {label.value}
          </Link>
        </li>
      );
    }
  );
  // If there are no items to display, just render an empty component.
  if (footerSubMenuItems?.length === 0) return null;

  return (
    <div className={S.content}>
      <ul className={S.list}>
        {!!footerSubMenuItems?.length && footerSubMenuItems}
      </ul>
      <div className={S.accessibilityLink}>
        <Link
          href={
            config.getSetting('accessibilityIcon.href').value ??
            accessibility?.href?.value
          }
          openInNewTab={config.getSetting('accessibilityIcon.newTab').value}
          prefetch={config.getSetting('accessibilityIcon.prefetch').value}
        >
          <img
            className={S.accessibilityImage}
            src={
              config.getSetting('accessibilityIcon.src').value ||
              '/images/Deckers_eA_Icon_transparent.png'
            }
            alt="essential accessibility"
            // This is at the bottom of the page, so it's not critical.
            loading="lazy"
            fetchPriority="low"
          />
        </Link>
      </div>
    </div>
  );
};
